import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { msg } from '@gonfalon/messages/msg';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type { DataTableAdditionalResults } from '../types';

import styles from '../DataTable.module.css';

type Props = {
  additionalResults: DataTableAdditionalResults;
  columnCount: number;
  headerElement: HTMLElement | null;
  isSticky: boolean;
  onHeightChange: (bannerHeight: number, headerHeight: number) => void;
};

const scrollsInline = (element: HTMLElement) => {
  const overflowX = getComputedStyle(element).overflowX;
  return overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay';
};

const inlineScrollers = (start: HTMLElement) => {
  const found: HTMLElement[] = [];
  let node: HTMLElement | null = start;
  while (node) {
    if (scrollsInline(node)) {
      found.push(node);
    }
    node = node.parentElement;
  }
  return found;
};

const inlineScrollport = (start: HTMLElement) => {
  const scrollers = inlineScrollers(start);
  return scrollers.find((element) => element.scrollWidth > element.clientWidth + 1) ?? scrollers[0] ?? start;
};

const resourceName = (count: number, resourceLabel: string) => (count === 1 ? resourceLabel : `${resourceLabel}s`);

const additionalResultsLabel = (results: DataTableAdditionalResults) => {
  if (results.pollingExpired) {
    return msg('ui.dataTable.additionalResults.pollingExpired');
  }

  const count = results.count >= results.maxCount ? `${results.maxCount}+` : String(results.count);
  return msg('ui.dataTable.additionalResults.label', {
    count,
    resource: resourceName(results.count, results.resourceLabel),
  });
};

export function DataTableAdditionalResultsRow({ additionalResults, ...rowProps }: Props) {
  const visible = additionalResults.count > 0 || Boolean(additionalResults.pollingExpired);
  const label = additionalResultsLabel(additionalResults);
  const [lastVisibleLabel, setLastVisibleLabel] = useState(label);
  if (visible && lastVisibleLabel !== label) {
    setLastVisibleLabel(label);
  }

  return (
    <DataTableHeaderBannerRow {...rowProps} variant="additionalResults">
      <div
        className={clsx(styles.additionalResults, !visible && styles.additionalResultsCollapsed)}
        aria-hidden={!visible}
      >
        <div className={styles.additionalResultsInner}>
          <Button
            variant="minimal"
            size="small"
            className={styles.additionalResultsButton}
            isDisabled={!visible}
            onPress={additionalResults.onAction}
          >
            <span className={styles.additionalResultsContent}>
              <Icon name="arrow-up-thin" size="small" style={{ fill: 'currentColor' }} />
              <span>{visible ? label : lastVisibleLabel}</span>
            </span>
          </Button>
        </div>
      </div>
    </DataTableHeaderBannerRow>
  );
}

type HeaderBannerRowProps = Omit<Props, 'additionalResults'> & {
  children: ReactNode;
  variant: 'additionalResults' | 'additionalFeedResults';
};

export function DataTableHeaderBannerRow({
  children,
  columnCount,
  headerElement,
  isSticky,
  onHeightChange,
  variant,
}: HeaderBannerRowProps) {
  const rowRef = useRef<HTMLElement | null>(null);
  const setRowElement = useCallback((node: HTMLElement | null) => {
    rowRef.current = node;
  }, []);

  useLayoutEffect(() => {
    if (!headerElement) {
      return;
    }
    const table = headerElement.parentElement;
    const headerCells = [...headerElement.querySelectorAll('[role="columnheader"]')];
    const headerRow = headerCells[0]?.closest('[role="row"]') ?? headerElement.firstElementChild;
    if (!table || !(headerRow instanceof HTMLElement)) {
      return;
    }

    const scrollers = inlineScrollers(table);
    const apply = () => {
      const scroller = inlineScrollport(table);
      const headerHeight = Math.round(
        Math.max(
          headerRow.getBoundingClientRect().height,
          ...headerCells.map((cell) => {
            const content = cell.firstElementChild;
            return Math.max(
              cell.getBoundingClientRect().height,
              content instanceof HTMLElement ? content.getBoundingClientRect().height : 0,
            );
          }),
        ),
      );
      headerElement.style.setProperty('--lp-data-table-header-block-size', `${headerHeight}px`);
      table.style.setProperty('--lp-data-table-viewport-inline-size', `${scroller.clientWidth}px`);
      table.style.setProperty('--lp-data-table-scroll-inline', `${scroller.scrollLeft}px`);
      const row = rowRef.current;
      onHeightChange(row ? Math.round(row.getBoundingClientRect().height) : 0, headerHeight);
    };

    apply();
    for (const scroller of scrollers) {
      scroller.addEventListener('scroll', apply, { passive: true });
    }
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        for (const scroller of scrollers) {
          scroller.removeEventListener('scroll', apply);
        }
      };
    }

    const observer = new ResizeObserver(apply);
    observer.observe(headerRow);
    observer.observe(headerElement);
    for (const cell of headerCells) {
      observer.observe(cell);
    }
    for (const scroller of scrollers) {
      observer.observe(scroller);
    }
    if (rowRef.current) {
      observer.observe(rowRef.current);
    }
    return () => {
      observer.disconnect();
      for (const scroller of scrollers) {
        scroller.removeEventListener('scroll', apply);
      }
      headerElement?.style.removeProperty('--lp-data-table-header-block-size');
      table.style.removeProperty('--lp-data-table-viewport-inline-size');
      table.style.removeProperty('--lp-data-table-scroll-inline');
    };
  }, [headerElement, onHeightChange]);

  useLayoutEffect(() => {
    return () => {
      onHeightChange(0, 0);
    };
  }, [onHeightChange]);

  if (!headerElement) {
    return null;
  }

  const isVirtualized = headerElement.tagName !== 'THEAD';
  const cellClassName = clsx(
    styles.additionalResultsCell,
    variant === 'additionalFeedResults' && styles.additionalFeedResultsCell,
    isSticky && !isVirtualized && styles.additionalResultsCellSticky,
  );
  const dataAttributes =
    variant === 'additionalResults' ? { 'data-additional-results': '' } : { 'data-additional-feed-results': '' };

  if (isVirtualized) {
    return createPortal(
      <div
        ref={setRowElement}
        className={clsx(styles.additionalResultsRow, styles.additionalResultsRowVirtual)}
        {...dataAttributes}
      >
        <div className={cellClassName}>{children}</div>
      </div>,
      headerElement,
    );
  }

  return createPortal(
    <tr ref={setRowElement} className={styles.additionalResultsRow} {...dataAttributes}>
      <td className={cellClassName} colSpan={columnCount}>
        {children}
      </td>
    </tr>,
    headerElement,
  );
}
